import { Component, OnDestroy, OnInit } from '@angular/core';
import { JeevesSalesCartListComponent, JeevesSalesCartListRow } from 'gung-standard-jeeves';
import {
  ProductService,
  CartService,
  Product,
  PriceService,
  AvailabilityService,
  SelectedCustomerService,
  AuthService,
  CartRow,
  GungModalService,
  Availability,
  CartRowPrice,
  Customer,
  GungFlowService
} from 'gung-standard';
import { DateUtilService } from 'gung-common';
import { TranslateService } from '@ngx-translate/core';
import { first, map, tap } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import { format, parse } from 'date-fns';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { TyloJeevesCartListStateService } from '../../../../services/tylo-jeeves-cart-list-state-service';
import { forkJoin, of } from 'rxjs';

const getMinDate = (av: Availability, quantity: number): Date => {
  if (av.currentAvailability > 0) {
    return new Date();
  }
  if (av.maxFutureStock === 0) {
    return new Date();
  }
  const valids = Object.keys(av.availabilities).filter(entry => av.availabilities[entry] >= quantity);
  const dateFormat = 'yyMMdd';
  return valids.length > 0 ? parse(valids[0], dateFormat, new Date()) : new Date();
};

@Component({
  selector: 'tylo-jeeves-sales-cart-list',
  templateUrl: './tylo-jeeves-sales-cart-list.component.html',
  styleUrls: ['./tylo-jeeves-sales-cart-list.component.css']
})
export class TyloJeevesSalesCartListComponent extends JeevesSalesCartListComponent implements OnInit, OnDestroy {
  selectedLanguageCode: string;
  isUser = false;

  deliveryDate = new Date();
  minDeliveryDate = new Date();
  partiallydelivery = true;
  availabilities: Availability[];

  constructor(
    protected authService: AuthService,
    protected productService: ProductService,
    protected cartService: CartService,
    protected priceService: PriceService,
    protected availabilityService: AvailabilityService,
    protected selectedCustomerService: SelectedCustomerService,
    protected dateUtilService: DateUtilService,
    protected translateService: TranslateService,
    private gungModalService: GungModalService,
    private datePipe: DatePipe,
    protected tyloJeevesCartListStateService: TyloJeevesCartListStateService,
    protected flowService: GungFlowService
  ) {
    super(productService, cartService, priceService, availabilityService, selectedCustomerService, dateUtilService, flowService);
  }

  ngOnInit() {
    this.authService
      .getCurrentUser()
      .pipe(first())
      .subscribe(currentUser => {
        if (currentUser.roles.includes('USER') && currentUser.roles.length === 1) {
          this.isUser = true;
        }

        super.ngOnInit();
        this.selectedLanguageCode = this.translateService.currentLang || 'se';
      });
  }

  

  updateData() {
    this.tyloJeevesCartListStateService.nextCartListIsLoading(true);
    const ids = this.data.map(c => c.productId);
    const cachedIds = Object.keys(this.cachedData);
    const toGetIds = ids.filter(id => !cachedIds.includes(id));

    const sub = forkJoin({
      products: this.productService.getProductsByIds(toGetIds).pipe(first()),
      prices: this.priceService.getCartRowPricesObservable(this.data).pipe(first()),
      availabilities: this.availabilities ? of(this.availabilities.map(av => ({ ...av, availabilities: av.extra.availabilities }))) : this.availabilityService.getAvailabilities(toGetIds, undefined, true).pipe(
        first(),
        map(avs => avs.map(av => ({ ...av, availabilities: av.extra.availabilities })))
      ),
      cartRows: of(this.data),
      customer: this.selectedCustomerService.getSelectedCustomer().pipe(first())
    })
      .pipe(
        first(),
        tap(resp => {
          this.availabilities = resp.availabilities;
          this.cachedData = {
            ...this.cachedData,
            ...toGetIds
              .map(id => ({
                product: resp.products.find(p => p.id === id),
                price: resp.prices.find(p => p.productId === id),
                availability: resp.availabilities.find(av => av.productId === id)
              }))
              .reduce((acc, curr) => ({ ...acc, [curr.product.id]: curr }), {})
          };
        }),
        map(data => {
          const cachedProducts = Object.values(this.cachedData).map(d => d.product);
          const cachedPrices = data.prices;
          const cachedAvs = Object.values(this.cachedData).map(d => d.availability);
          return this.mapData(cachedProducts, cachedPrices, cachedAvs, data.cartRows, data.customer);
        })
      )
      .subscribe(d => this.updateMappedData(d));

    this.subscriptions.push(sub);

    const deliveryChoice = localStorage.getItem('deliveryChoice');
    if (deliveryChoice === '10') {
      this.partiallydelivery = false;
    } else {
      this.partiallydelivery = true;
    }
  }

  public _mapData(
    products: Product[],
    prices: CartRowPrice[],
    availabilities: Availability[],
    cartRows: CartRow[],
    customer: Customer
  ): JeevesSalesCartListRow[] {
    const mapData = super.mapData(products, prices, availabilities, cartRows, customer);
    this.assignRowDates(mapData);
    return mapData;
  }

  editNote(row: CartRow): void {
    let noteInput = '';
    if (!!row.extra.orp) {
      noteInput = row.extra.orp.orpradtext || '';
    }

    this.gungModalService.openEditNoteModal(noteInput).then(
      (result: any) => {
        if (!!result && result.hasOwnProperty('note')) {
          this.cartService.setExtra(
            row.productId,
            {
              orp: {
                orpradtext: result.note
              }
            },
            row.targetStockId,
            row.productPartialId
          );
        }
      },
      reason => {}
    );
  }

  dateSelected(event) {
    const date = event.date;
    for (const row of this.mappedData) {
      let rowDate = (row.cartRow.extra.orp && row.cartRow.extra.orp.ordberlevdat) || row.deliveryDate || row.minDate;
      if (typeof rowDate === 'string') {
        rowDate = parse(rowDate, 'yyyy-MM-dd', new Date());
      } else if (rowDate instanceof NgbDate) {
        rowDate = new Date(rowDate.year, rowDate.month - 1, rowDate.day);
      }

      if (rowDate < date) {
        this.cartService.removeExtraField(row.productId, 'orp.ordberlevdat', row.targetStockId);
        const dateFortmated = format(date, 'yyyy-MM-dd');
        row.deliveryDate = dateFortmated;
        this.cartService.setExtra(
          row.productId,
          {
            orp: {
              ordberlevdat: dateFortmated
            }
          },
          row.targetStockId,
          row.productPartialId
        );
      }
      // this.assignRowDates(row.productId);
    }
  }

  restoreMinDate() {
    for (const row of this.mappedData) {
      const rowDate = new Date(row.minDate.year, row.minDate.month - 1, row.minDate.day);

      this.cartService.removeExtraField(row.productId, 'orp.ordberlevdat', row.targetStockId);
      const dateFortmated = format(rowDate, 'yyyy-MM-dd');
      row.deliveryDate = dateFortmated;
      this.cartService.setExtra(
        row.productId,
        {
          orp: {
            ordberlevdat: dateFortmated
          }
        },
        row.targetStockId,
        row.productPartialId
      );
      // this.assignRowDates(row.productId);
    }
    this.deliveryDate = new Date();
  }

  updateDeliveryDate(row: JeevesSalesCartListRow, selectedDeliveryDate) {
    const oldVal = row.deliveryDate || row.minDate;

    const deliveryDate = format(selectedDeliveryDate.date, 'yyyy-MM-dd');

    if (oldVal === deliveryDate) {
      return;
    }

    if (!deliveryDate) {
      this.cartService.removeExtraField(row.productId, 'orp.ordberlevdat', row.targetStockId);
      return;
    }

    this.cartService.setExtra(
      row.productId,
      {
        orp: {
          ordberlevdat: deliveryDate
        },
        _user_selected_ordberlevdat: deliveryDate
      },
      row.targetStockId,
      row.productPartialId
    );

    // this.assignRowDates(row.productId);
  }

  addWorkdays(date: Date, days: number) {
    let daysRemaining = days;
    while (daysRemaining > 0) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        daysRemaining--;
      }
    }
  }

  setMinDeliveryDate(rows: JeevesSalesCartListRow[]) {
    const now = new Date();
    let minDate = new NgbDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
    for (const row of rows) {
      if (!minDate || row.minDate.before(minDate)) {
        minDate = row.minDate;
      }
    }
    this.minDeliveryDate = this.dateUtilService.createDateFromNgbDate(minDate);
  }

  assignRowDates(mappedData: JeevesSalesCartListRow[]): void {
    if (!mappedData.length) {
      this.tyloJeevesCartListStateService.nextCartListIsLoading(false);
      return;
    }
    // check if its not partially delivery
    for (const rowData of mappedData) {
      const row = rowData.cartRow;
      if (row.extra._dellevtillaten === '10') {
        this.tyloJeevesCartListStateService.nextCartListIsLoading(false);
        return;
      }
    }
    const avs = this.availabilities;
      for (const rowData of mappedData) {
        const row = rowData.cartRow;
        const av = avs.find(a => a.productId === row.productId);
        const newExtra = JSON.parse(JSON.stringify(row.extra));
        newExtra._availability_reference = av;
        let mindate = null;
        if (av) {
          const tempDate = getMinDate(av, 1);
          let minDate = new Date();
          if (minDate < tempDate) {
            minDate = tempDate;
          }
          const dString: string = this.datePipe.transform(minDate, 'yyyy-MM-dd');
          mindate = dString;
        }
        newExtra._calc_min_date = mindate;
        newExtra.orp = !!newExtra.orp ? newExtra.orp : {};
        newExtra.orp.ordberlevdat = mindate;
        if (newExtra._user_selected_ordberlevdat) {
          if (newExtra._user_selected_ordberlevdat > newExtra._calc_min_date) {
            newExtra.orp.ordberlevdat = newExtra._user_selected_ordberlevdat;
          }
        }

        const deliveryPlan = {};
        const dates = Object.keys(av.availabilities);
        let quantityRemaining = row.qty;
        for (const date of dates) {
          const qty = av.availabilities[date];
          if (qty > 0 && quantityRemaining > 0) {
            const parsedDate = parse(date, 'yyMMdd', new Date());
            const formattedDate = this.datePipe.transform(parsedDate, 'yyyy-MM-dd');

            let assignedDate = formattedDate;
            if (newExtra._user_selected_ordberlevdat > assignedDate) {
              assignedDate = newExtra._user_selected_ordberlevdat;
            }
            const currentOnDate = deliveryPlan[assignedDate] || 0;
            const currentAllocation = Math.min(qty, quantityRemaining);

            deliveryPlan[assignedDate] = currentOnDate + currentAllocation;

            quantityRemaining = quantityRemaining - currentAllocation;
          }
        }

        /* This is for partial delivery where the first load should be delivered as quick as possible and the second later on the new date
        const planningDates = Object.keys(deliveryPlan);
        if (planningDates.length > 0) {
          const lastDeliveryDate = planningDates[planningDates.length - 1];
          if (row.extra._user_selected_ordberlevdat > lastDeliveryDate) {
            deliveryPlan[row.extra._user_selected_ordberlevdat] = deliveryPlan[lastDeliveryDate];
            delete deliveryPlan[lastDeliveryDate];
          }
        }
        */

        if (quantityRemaining > 0) {
          // Handle edge case of last quantities of exporing product
          deliveryPlan['2099-12-31'] = quantityRemaining;
        }

        let _deliveryPlan = row.extra._deliveryPlan ? JSON.parse(JSON.stringify(row.extra._deliveryPlan)) : {};

        for (const prop in _deliveryPlan) {
          if (_deliveryPlan.hasOwnProperty(prop)) {
            _deliveryPlan[prop] = undefined;
          }
        }

        _deliveryPlan = {
          ..._deliveryPlan,
          ...deliveryPlan
        };

        if (JSON.stringify(_deliveryPlan) !== JSON.stringify(row.extra._deliveryPlan)) {
          this.cartService.setExtra(
            row.productId,
            {
              _deliveryPlan
            },
            row.targetStockId,
            row.productPartialId
          );
        }
        /* row.extra._deliveryPlan = _deliveryPlan; */

        // this.cartService.setExtra(row.productId, {
        //   _deliveryPlan
        // }, row.targetStockId, row.productPartialId);
      }
      this.tyloJeevesCartListStateService.nextCartListIsLoading(false);
  }

  showDeliveryPlan(_deliveryPlan) {
    let message = `
    <div class="table-responsive">
      <table class="table table-striped">
        <thead>
          <tr>
            <th scope="col" translate>DATE</th>
            <th scope="col" translate>QUANTITY</th>
          </tr>
        </thead>
        <tbody>
    `;
    const dates = Object.keys(_deliveryPlan).sort((a, b) => (a > b ? 1 : -1));
    for (const prop of dates) {
      if (_deliveryPlan.hasOwnProperty(prop)) {
        message += `<tr><th scope="row">${prop}</th><td>${_deliveryPlan[prop]}</td></tr>`;
      }
    }
    message += `
        </tbody>
      </table>
    </div>
    `;
    this.gungModalService.openConfirmYesNoModal('', message, {}, 'OK', null, true).then();
  }

  public mapData(
    products: Product[],
    prices: CartRowPrice[],
    availabilities: Availability[],
    cartRows: CartRow[],
    customer: Customer
  ): JeevesSalesCartListRow[] {
    const mapData =  cartRows.map(cr => {
      const product = products.find(p => p.id === cr.productId);
      const price = prices.find(
        p => p.productId === cr.productId && (!p.productPartialId || p.productPartialId === cr.productPartialId)
      );
      // const av = this.currentFlowRequireAvailability ? availabilities.find(a => a.productId === cr.productId) : null;
      const av = availabilities.find(a => a.productId === cr.productId);
      const minDateJs: Date = this.getMinDate(av);
      const toReturn: JeevesSalesCartListRow = {
        productId: cr.productId,
        productPartialId: cr.productPartialId,
        name: product.name,
        quantity: cr.qty,
        originalPrice: price.customerGrossPrice.value,
        currency: price.customerNetPrice.currencyCode,
        minDate: new NgbDate(minDateJs.getFullYear(), minDateJs.getMonth() + 1, minDateJs.getDate()),
        targetStockId: cr.targetStockId,
        cartRowPriceElement: price,
        deliveryMethod: customer.extra.kus.levsattkod,
        cartRow: cr,
        currentAvailability: this.currentFlowRequireAvailability ? av.currentAvailability : 0,
        product,
        price
      };
      if (cr.extra && cr.extra.orp && cr.extra.orp.rabatt1) {
        toReturn.discountPercent = cr.extra.orp.rabatt1;
      }
      if (cr.extra && cr.extra.orp && cr.extra.orp.rabatt2) {
        toReturn.discountPercent2 = cr.extra.orp.rabatt2;
      }
      if (cr.extra && cr.extra.orp && cr.extra.orp.vb_pris) {
        toReturn.overridePrice = cr.extra.orp.vb_pris;
      }
      if (cr.extra && cr.extra.orp && cr.extra.orp.artbeskr) {
        toReturn.description = cr.extra.orp.artbeskr;
      }
      if (this.getDeliveryDateFromRow(cr)) {
        const setDate = new Date(this.getDeliveryDateFromRow(cr));
        toReturn.deliveryDate = format(setDate, 'yyyy-MM-dd');
      }
      return toReturn;
    });
    this.assignRowDates(mapData);
    return mapData;
  }

}
